import { useMemo } from 'react'
import { client, segmentClient } from '@/analytics/segmentClient'
import { useSellerInfo } from '@/proxy/seller/hooks'
import { SellerLocation } from '@/resources/types/seller'
import { STORE_OPEN_STATUS } from '@/resources/utils'

export type SellerAnalyticsContext = {
  device_id: string | null
  poc_id: string
  uf: string
  city: string
  coordinates: [number, number]
  store_is_open: boolean
}

export type SellerAnalyticsMinimalContext = Pick<
  SellerAnalyticsContext,
  'poc_id' | 'store_is_open'
>

export type SellerContextTrackSetup = {
  minimalContext?: boolean
  omitKeys?: string[]
}

type SellerInfoResponse = {
  seller?: {
    id?: number
    address?: {
      provinceCode?: string
      city?: string
      location?: { coordinates?: [number, number] }
    }
  }
}

type SellerContextTrackFn = (
  properties?: { [key: string]: unknown },
  setup?: SellerContextTrackSetup
) => boolean

export type SellerContextClient = {
  [methodName in keyof typeof segmentClient]: SellerContextTrackFn
}

type SellerAnalyticsFieldsGetter = (
  extension?: { [key: string]: unknown },
  minimalContext?: boolean
) => { [key: string]: unknown } | null

const withoutKeys = (
  properties: { [key: string]: unknown },
  keys: string[]
) => {
  const copy = { ...properties }

  for (const key of keys) {
    delete copy[key]
  }

  return copy
}

export const buildMinimalSellerAnalyticsContext = (
  seller: SellerLocation | null | undefined,
  isSellerInfoFetched: boolean
): SellerAnalyticsMinimalContext | null => {
  if (!seller || !isSellerInfoFetched) {
    return null
  }

  return {
    poc_id: seller.id?.toString() ?? '',
    store_is_open: localStorage.getItem(STORE_OPEN_STATUS) === 'true',
  }
}

export const buildSellerAnalyticsContext = (
  seller: SellerLocation | null | undefined,
  isSellerInfoFetched: boolean
): SellerAnalyticsContext | null => {
  if (!seller || !isSellerInfoFetched) {
    return null
  }

  const {
    id: pocId = '',
    address: {
      provinceCode: uf = '',
      city = '',
      location: { coordinates = [0, 0] as [number, number] } = {},
    } = {},
  } = seller

  return {
    device_id: client.instance?.user()?.anonymousId() ?? null,
    poc_id: pocId.toString(),
    uf,
    city,
    coordinates,
    store_is_open: localStorage.getItem(STORE_OPEN_STATUS) === 'true',
  }
}

export const useSellerAnalyticsFields = (): SellerAnalyticsFieldsGetter => {
  const { data, isFetched: isSellerInfoFetched } = useSellerInfo()
  const seller = (data as SellerInfoResponse | undefined)?.seller as
    | SellerLocation
    | undefined

  return (
    extension: { [key: string]: unknown } = {},
    minimalContext = false
  ) => {
    const context = minimalContext
      ? buildMinimalSellerAnalyticsContext(seller, isSellerInfoFetched)
      : buildSellerAnalyticsContext(seller, isSellerInfoFetched)

    return context ? { ...context, ...extension } : null
  }
}

export const useAnalyticsSellerContext = (): SellerContextClient => {
  const getSellerFields = useSellerAnalyticsFields()

  return useMemo(
    () =>
      new Proxy(segmentClient, {
        get(target, prop: string | symbol) {
          if (typeof prop !== 'string') {
            return Reflect.get(target, prop) as unknown
          }

          const original = Reflect.get(target, prop) as unknown

          if (typeof original !== 'function') {
            return original
          }

          return (
            extension: { [key: string]: unknown } = {},
            setup: SellerContextTrackSetup = {}
          ) => {
            const { minimalContext = false, omitKeys = [] } = setup
            const payload = getSellerFields(extension, minimalContext)

            if (!payload) {
              return false
            }

            const event =
              omitKeys.length > 0 ? withoutKeys(payload, omitKeys) : payload

            ;(original as (props: { [key: string]: unknown }) => void)(event)
            return true
          }
        },
      }) as unknown as SellerContextClient,
    [getSellerFields]
  )
}
